import React from 'react';
import {
  ButtonText,
  HStack,
  Pressable,
  ScrollView,
  VStack,
  Heading,
  View,
  Icon,
  Button,
  Box,
  FormControlLabel,
  FormControlLabelText,
} from '@gluestack-ui/themed';
import { Modal } from 'react-native';
import { X } from 'lucide-react-native';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import FormField from '@/common/components/forms/FormField';
import log from '@/common/utils/logger';
import { AdminObjectName } from './DataColumnConfig';
import useData from '../hooks/useData';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNestedValue = (obj: Record<string, any>, path: string): any => {
  const result = path.split('.').reduce((acc, part) => {
    if (acc && typeof acc === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (acc as Record<string, any>)[part];
    }
    return undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, obj as any);
  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setNestedValue = (obj: Record<string, any>, path: string, value: any): void => {
  const keys = path.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: Record<string, any> = obj;
  while (keys.length > 1) {
    const key = keys.shift();
    if (!key) break;
    if (!current[key]) current[key] = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    current = current[key] as Record<string, any>;
  }
  const finalKey = keys[0];
  if (finalKey) {
    current[finalKey] = value;
  }
};

interface LookupConfig {
  objectName: AdminObjectName;
  customQueryKey?: string;
  filter?: Record<string, unknown>;
  label: (item: unknown) => string;
}

/**
 * Props that custom components in EditMemberModal will receive
 * This ensures type safety for custom field components
 */
export interface CustomFieldComponentProps<T = unknown> {
  /** The current value from react-hook-form */
  value: T;
  /** Callback to update the value in react-hook-form */
  onChange: (value: T) => void;
  /** The complete member data object (read-only for context) */
  memberData: unknown;
  /** The parent entity ID (e.g., courseId) */
  parentId?: string;
  /** Error state from react-hook-form */
  error?: string;
}

interface ColumnConfig {
  label?: string;
  type?: string;
  rules?: Record<string, unknown>;
  horizontal?: boolean;
  fieldWidth?: string | number;
  choices?: Array<{ label: string; value: string }>;
  placeholder?: string;
  lookup?: LookupConfig;
  /**
   * Custom component for rendering complex fields.
   * Component receives CustomFieldComponentProps via react-hook-form's Controller.
   *
   * For a field that stores one or a few ids and should search on the server,
   * render `ReferenceListField` or `ReferenceSelectField` here. Do not use
   * `lookup` for that — lookup loads a page of 1000 and filters in the browser.
   * Guide: app/docs/architecture/admin-linking-patterns.md
   */
  component?: React.ComponentType<CustomFieldComponentProps>;
  /**
   * Additional read-only props to pass to the custom component.
   * These should NOT be used for bidirectional state synchronization.
   * Use the onBeforeSave callback pattern instead.
   */
  componentProps?: Record<string, unknown>;
  transform?: {
    toForm?: (value: unknown, row: unknown) => unknown;
    fromForm?: (value: unknown) => unknown;
  };
}

interface LookupFormFieldProps {
  fieldName: string;
  field: ColumnConfig;
}

const LookupFormField: React.FC<LookupFormFieldProps> = ({ fieldName, field }) => {
  const { data, isLoading } = useData({
    objectName: field.lookup!.objectName,
    customQueryKey: field.lookup?.customQueryKey || `editMemberModal-${fieldName}-${field.lookup!.objectName}`,
    filter: field.lookup?.filter || {},
    currentPage: 1,
    currentPageSize: 1000,
  });

  if (isLoading) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const choices = (data as Array<{ id: string }>)?.map((item: any) => ({ label: field.lookup!.label(item), value: String(item.id) })) || [];

  return (
    <FormField
      name={fieldName}
      label={field?.label || fieldName}
      type="select"
      choices={choices}
      rules={field?.rules || {}}
      horizontal={field?.horizontal}
      fieldWidth={field?.fieldWidth}
    />
  );
};

/**
 * Callback data structure for onBeforeSave hook.
 * Allows custom components to provide additional updates that should be applied
 * to parent state when the modal is saved.
 */
export interface BeforeSaveResult {
  /** Updates to apply to the parent form/component state (e.g., segment reordering) */
  parentStateUpdates?: Record<string, unknown>;
}

interface EditMemberModalProps {
  visible?: boolean;
  hideModal: () => void;
  /**
   * Called when the user saves changes in the modal.
   * The modal guarantees this is only called after successful validation.
   */
  onEditMember?: (variables: {
    id: string | number;
    data: Record<string, unknown>;
    /** Optional updates to parent state, collected from custom components */
    beforeSaveResult?: BeforeSaveResult;
  }) => Promise<void>;
  columns: Record<string, ColumnConfig>;
  title?: string;
  memberData?: Record<string, unknown>;
  parentId?: string;
  /**
   * Hook called before save to allow custom components to provide additional updates.
   * This is the recommended pattern for complex components that need to affect parent state.
   */
  onBeforeSave?: () => BeforeSaveResult | Promise<BeforeSaveResult>;
}

/**
 * EditMemberModal component for editing membership properties.
 *
 * ## Architecture: Modal as Independent Editor
 *
 * This component implements the "Modal as Independent Editor" pattern:
 * 1. Works with a deep copy of memberData (independent state)
 * 2. Changes are staged locally in react-hook-form
 * 3. Changes are committed ONLY on explicit Save
 * 4. Cancel discards all changes without side effects
 *
 * ## Type Safety
 *
 * Custom components receive `CustomFieldComponentProps` with:
 * - `value` / `onChange` from react-hook-form
 * - `memberData` (read-only context)
 * - `parentId` (read-only context)
 * - `error` (validation error if any)
 * - Any additional `componentProps` from column config
 *
 * A custom field that points at other records (extra modules, cohort, course)
 * should be a reference field, not `type: 'lookup'`. Lookup loads up to 1000
 * rows. Reference fields search through `getList` and save with this dialog.
 * See app/docs/architecture/admin-linking-patterns.md.
 *
 * ## Usage Example
 *
 * ```typescript
 * const columns = {
 *   name: { label: 'Name', type: 'text' },
 *   customField: {
 *     type: 'custom',
 *     component: MyCustomComponent,
 *     componentProps: { extraData: 'readonly' },
 *   },
 * };
 *
 * <EditMemberModal
 *   visible={isOpen}
 *   hideModal={close}
 *   columns={columns}
 *   memberData={currentMember}
 *   onEditMember={async ({ id, data }) => {
 *     await updateAPI(id, data);
 *     // Handle any parent state updates based on committed data
 *   }}
 * />
 * ```
 *
 * @see CustomFieldComponentProps for custom component interface
 * @see docs/refactoring-edit-member-modal-2026-01.md for migration guide
 */
export const EditMemberModal: React.FC<EditMemberModalProps> = ({
  visible = false,
  hideModal,
  onEditMember,
  columns,
  title = 'Edit Membership Properties',
  memberData = {},
  parentId,
  onBeforeSave,
}) => {
  const methods = useForm();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  /**
   * Form submission handler.
   * Pattern: Modal works with local form state and only commits changes on explicit Save.
   * This ensures Cancel can safely discard all changes without side effects.
   */
  const onSubmit = async (formData: Record<string, unknown>) => {
    setIsSubmitting(true);

    // Apply fromForm transformers for fields, where defined
    Object.entries(columns).forEach(([fieldName, field]) => {
      if (field?.transform?.fromForm) {
        const fromValue = getNestedValue(formData, fieldName);
        const toValue = field.transform.fromForm(fromValue);
        setNestedValue(formData, fieldName, toValue);
      }
    });

    try {
      // Collect any additional updates from custom components via the onBeforeSave hook
      const beforeSaveResult = onBeforeSave ? await onBeforeSave() : undefined;

      if (onEditMember) {
        await onEditMember({
          id: (memberData as { id: string | number }).id,
          data: formData,
          beforeSaveResult,
        });
      }
      hideModal();
    } catch (error) {
      log.error('Error submitting member edit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Initialize form state when modal opens.
   * Creates a deep copy to ensure modal works with independent state.
   * This guarantees that Cancel can discard changes without affecting parent state.
   */
  React.useEffect(() => {
    if (!visible) return;

    // Create a deep copy of memberData to ensure modal independence
    const formData: Record<string, unknown> = JSON.parse(JSON.stringify(memberData || {}));

    Object.entries(columns).forEach(([fieldName, field]) => {
      let nestedValue = getNestedValue(memberData as Record<string, unknown>, fieldName);

      // Apply toForm transformers for fields where defined
      if (field?.transform?.toForm) {
        try {
          nestedValue = field.transform.toForm(nestedValue, memberData);
        } catch (e) {
          log.info('Error transforming field', fieldName, e);
          nestedValue = '';
        }
      }

      // Explicitly set to null if undefined to ensure proper form reset
      setNestedValue(formData, fieldName, nestedValue !== undefined ? nestedValue : null);
    });

    // Reset form with fresh data, discarding any previous state
    methods.reset(formData, { keepValues: false, keepDefaultValues: false });
  }, [memberData, visible, columns, methods]);

  return (
    <Modal animationType="none" transparent visible={visible} onRequestClose={() => hideModal()}>
      <View
        flex={1}
        bg="rgba(0, 0, 0, 0.5)"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          $base-w="95%"
          $md-w={700}
          $lg-w={900}
          maxHeight="90%"
          bg="$white"
          borderRadius={12}
          overflow="hidden"
          softShadow="4"
        >
          <VStack p={24} space="xl">
            <HStack height={40} alignItems="center" justifyContent="space-between" borderBottomWidth={1} borderBottomColor="$borderLight" pb="$3">
              <Heading size="sm" color="$primary600">
                {title}
              </Heading>
              <Pressable testID="button-close-video-modal" onPress={() => hideModal()} p="$1">
                <Icon as={X} size="xl" color="$textSubtle" />
              </Pressable>
            </HStack>

            <ScrollView flexShrink={1} showsVerticalScrollIndicator={false}>
              <FormProvider {...methods}>
                <VStack space="xl" py="$2">
                  {
                    Object.entries(columns).map(([fieldName, field]) => {
                      if (field?.lookup) {
                        return (
                          <Box key={fieldName} px="$1">
                            <LookupFormField fieldName={fieldName} field={field} />
                          </Box>
                        );
                      }

                      if (field?.type === 'custom' && field?.component) {
                        const CustomComponent = field.component;
                        const itemId = (memberData as { id?: string | number })?.id;
                        return (
                          <Box key={`${fieldName}-${itemId || 'new'}`} px="$1">
                            {!!field?.label && (
                              <FormControlLabel mb="$2">
                                <FormControlLabelText size="xs" fontWeight="$bold" color="$textSubtle" textTransform="uppercase">
                                  {field?.label}
                                </FormControlLabelText>
                              </FormControlLabel>
                            )}
                            <Controller
                              key={`${fieldName}-controller-${itemId || 'new'}`}
                              control={methods.control}
                              name={fieldName}
                              rules={field?.rules || {}}
                              render={({ field: formField, fieldState }) => (
                                <CustomComponent
                                  value={formField.value}
                                  onChange={formField.onChange}
                                  memberData={memberData}
                                  parentId={parentId}
                                  error={fieldState.error?.message}
                                  {...(field.componentProps || {})}
                                />
                              )}
                            />
                          </Box>
                        );
                      }

                      let fieldType: 'text' | 'checkbox' | 'select' | 'number' | 'textarea' | 'switch' | 'radio' | 'rte' = 'text';
                      if (field?.type === 'bool') fieldType = 'checkbox';
                      else if (field?.type === 'select') fieldType = 'select';
                      else if (field?.type === 'number') fieldType = 'number';

                      return (
                        <Box key={fieldName} px="$1">
                          <FormField
                            name={fieldName}
                            label={field?.label || fieldName}
                            type={fieldType}
                            choices={field?.choices}
                            rules={field?.rules || {}}
                            horizontal={field?.horizontal}
                            fieldWidth={field?.fieldWidth || '100%'}
                            placeholder={field?.placeholder}
                            labelSize="sm"
                            fieldSize="sm"
                          />
                        </Box>
                      );
                    })
                  }
                </VStack>
              </FormProvider>
            </ScrollView>

            <HStack space="md" justifyContent="flex-end" pt="$4" borderTopWidth={1} borderTopColor="$borderLight">
              <Button variant="outline" action="secondary" onPress={hideModal} size="md" borderRadius="$lg" isDisabled={isSubmitting}>
                <ButtonText>Cancel</ButtonText>
              </Button>
              <Button onPress={methods.handleSubmit(onSubmit)} size="md" borderRadius="$lg" action="primary" isDisabled={isSubmitting}>
                <ButtonText>{isSubmitting ? 'Saving...' : 'Save Changes'}</ButtonText>
              </Button>
            </HStack>
          </VStack>
        </Box>
      </View>
    </Modal>
  );
};

export default EditMemberModal;
